"use client";

import { MapPin, Phone, Mail } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function GetInTouch() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  return <section ref={ref} className={`py-16 px-6 bg-light-background transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
      <div className="max-w-7xl mx-auto">
        <h2 className="text-4xl font-display font-bold text-foreground-light mb-12 text-center">
          Get in Touch
        </h2>

        <div className="grid md:grid-cols-2 gap-12 items-start">
          {}
          <div className="space-y-8">
            <ContactInfoItem icon={<MapPin className="size-6 text-primary" />} label="Address" value="203 Surrey Ct, Smithville, MO 64089" />
            <ContactInfoItem icon={<Phone className="size-6 text-primary" />} label="Phone" value="816-226-6370" />
            <ContactInfoItem icon={<Mail className="size-6 text-primary" />} label="Email" value="info@firstclassresources.com" />
          </div>

          {}
          <FormProvider className="rounded-lg shadow-soft p-6 bg-background border border-muted transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)]" style={{
          transform: isVisible ? "translateY(0)" : "translateY(1.25rem)",
          opacity: isVisible ? 1 : 0
        }} formId="contact-form-hero" sectionName="get-in-touch" defaultValues={{
          name: "",
          email: "",
          phone: "",
          message: ""
        }} validationRules={{
          name: {
            required: true,
            minLength: 2,
            maxLength: 50
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: false,
            pattern: "phone"
          },
          message: {
            required: true,
            minLength: 10
          }
        }} submitText="Send Message" successMessage="Thank you for reaching out! We'll respond within 24 hours.">
            <ContactForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function ContactInfoItem({
  icon,
  label,
  value
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return <div className="flex items-start gap-4 group transition-transform duration-[var(--duration-fast)] ease-[var(--ease-spring)] hover:-translate-y-1">
      <div className="mt-1">{icon}</div>
      <div>
        <p className="text-sm font-medium text-muted-foreground mb-1">{label}</p>
        <p className="text-base text-foreground-light font-sans">{value}</p>
      </div>
    </div>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-primary-light border border-primary rounded-lg">
        <p className="text-foreground-primary mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="w-full">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-4">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Your Name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="you@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[0-9\s\-\(\)\+]+$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone (Optional)</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="123-456-7890" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Message is required",
        minLength: {
          value: 10,
          message: "Message must be at least 10 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Message</FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us how we can help..." className="min-h-32" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-destructive/10 border border-destructive rounded-lg">
            <p className="text-destructive text-sm">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full transition-all duration-[var(--duration-fast)] hover:scale-[1.02]">
          {isSubmitting ? <>
              <span className="animate-spin mr-2">⏳</span>
              Sending...
            </> : "Send Message"}
        </Button>
      </div>
    </Form>;
}